import React from "react";
import { useMsal } from "@azure/msal-react";
import { getConfig } from "../../configuration";
import getOktaInstance from "../../auth/oktaConfig";
import { forgetSignInHints, markExplicitlySignedOut } from "../../auth/loginPreferences";
import Button from "./Button";

interface LogoutButtonInterface {
  oktaIdToken?: string;
  setIsLoading: (isLoading: boolean) => void;
  userRemoved: () => void;
  identityProvider: string;
}

const LogoutButton: React.FC<LogoutButtonInterface> = ({ oktaIdToken, setIsLoading, userRemoved, identityProvider }) => {
  const { instance } = useMsal();

  const handleLogout = async () => {
    switch (identityProvider) {
      case "Entra":
        await handleEntraLogout();
        break;
      case "Okta":
        await handleOktaLogout();
        break;
      default:
        console.error("Unknown identity provider:", identityProvider);
    }
  }

  const handleEntraLogout = async () => {
    setIsLoading(true);
    try {
      // `logoutPopup` resolves on success and rejects on dismissal, so it needs no corroboration.
      // An `ssoSilent` probe used to second-guess it and read a still-live second session as a
      // cancellation, which meant Sign out did nothing at all for a user with two Entra profiles.
      await instance.logoutPopup();
      userRemoved();
      markExplicitlySignedOut();
      forgetSignInHints();
      console.log("Logout successful.");
    } catch (error) {
      console.error('Logout failed:', error);
    } finally {
      setIsLoading(false);
    }
  };

  const handleOktaLogout = async () => {
    setIsLoading(true);
    try {
      const oktaInstance = getOktaInstance();
      try {
        await oktaInstance.revokeAccessToken();
      } catch (error) {
        console.warn("Okta access token revocation failed:", error);
      }
      try {
        // Ends the Okta session silently (no page navigation), keeping the user inside Excel.
        await oktaInstance.closeSession();
      } catch (error) {
        // Fallback: run the OIDC logout endpoint inside an Office dialog instead of the browser.
        console.warn("Silent Okta session close failed, falling back to dialog logout:", error);
        openOktaLogoutDialog();
      }
      // Required now these outlive the pane, or the next load reuses a revoked pair.
      oktaInstance.tokenManager.clear();
      userRemoved();
      markExplicitlySignedOut();
      forgetSignInHints();
      console.log("Okta Logout successful.");
    } catch (error) {
      console.error("Okta Logout failed:", error);
    } finally {
      setIsLoading(false);
    }
  };

  const openOktaLogoutDialog = () => {
    const oktaConfig = getConfig().okta;
    if (!oktaConfig.issuer) {
      throw new Error("Okta issuer is not configured");
    }
    const logoutUrl = `${oktaConfig.issuer}/v1/logout` +
      `?id_token_hint=${oktaIdToken}` +
      `&post_logout_redirect_uri=${encodeURIComponent(oktaConfig.postLogoutRedirectUriBase + "logout-okta.html")}`;
    const bridgeUrl = `${oktaConfig.postLogoutRedirectUriBase}logout-okta.html?u=${encodeURIComponent(logoutUrl)}`;
    Office.context.ui.displayDialogAsync(bridgeUrl, { height: 45, width: 35 }, (result) => {
      if (result.status === Office.AsyncResultStatus.Succeeded) {
        const dialog = result.value;
        dialog.addEventHandler(Office.EventType.DialogMessageReceived, () => dialog.close());
      } else {
        console.error("Okta logout dialog failed to open:", result.error);
      }
    });
  };

  return <Button label="Sign out" onClick={handleLogout} />;
};

export default LogoutButton;
